import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Memoize } from 'lodash-decorators';
import {
  DefaultsState,
  DefaultsStateKey,
  MedusaUser,
  TeamMember,
  UseDefaultsStateKey,
  UserInfo
} from '@approot/models';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService } from '../auth/auth.service';
import { ProductsConfigService } from '../products-config/products-config.service';
import { UserStorageService } from '../user-storage/user-storage.service';
import { isTrueLike } from '@approot/utilities';

type ImageData = {
  url: string;
  name: string;
};

type ImageURLs = {
  mainLogo: ImageData;
};

type MedusaAccount = Partial<{
  userId: string;
  sessionId: string;
  timestamp: number;
  entity: string;
}>;

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  public medusaAccount: MedusaAccount = {};
  public medusaCredentials: MedusaUser[];
  public showMedusaLoginErrorModal: boolean;

  private _teamMembers: TeamMember[];
  private _userInfo: UserInfo;
  private _imagesUrls: ImageURLs = {
    mainLogo: {
      name: 'digital-vega',
      url: 'assets/digitalvega.png'
    }
  };

  constructor(
    private alertsService: AlertsService,
    private productsConfigService: ProductsConfigService,
    private authService: AuthService,
    private userStorageService: UserStorageService
  ) {}

  public get teamMembers() {
    return this._teamMembers || [];
  }

  public get userInfo(): UserInfo {
    return this._userInfo;
  }

  public get medusaUser(): MedusaUser {
    const entity = this.medusaAccount?.entity;
    const user = this.medusaCredentials?.find(c => c.entity === entity);
    return user;
  }

  public setUserInfo() {
    this.authService.currentSession().then(data => {
      const token = data.getIdToken()?.payload;
      if (token)
        this._userInfo = {
          phone_number: token.phone_number,
          email: token.email,
          groups: token['cognito:groups'],
          username: token['cognito:username'],
          isWhiteLabelUser: isTrueLike(token['custom:isWhiteLabelUser'])
        };
    });
  }

  public get isMedusaAccountLoggedIn() {
    return !!this.medusaAccount?.sessionId;
  }

  public setImagesUrl(item: keyof ImageURLs, imageData: ImageData) {
    this._imagesUrls[item].url = imageData.url;
    this._imagesUrls[item].name = imageData.name;
  }

  public getImageUrl(item: keyof ImageURLs) {
    return this._imagesUrls[item];
  }

  public get useDefaults(): boolean {
    const useDefaults =
      this.userStorageService.getItem<boolean>(UseDefaultsStateKey);
    if (useDefaults == null) {
      return true;
    }
    try {
      return !!useDefaults;
    } catch {
      return true;
    }
  }

  public get attributeDefaults(): DefaultsState {
    const defaults =
      this.userStorageService.getItem<DefaultsState>(DefaultsStateKey) || [];
    this.formatLoadedDefaults(defaults); //format certain fields
    return defaults;
  }

  @Memoize()
  public getUserEmail(userId: string): string {
    if (!userId) {
      return '';
    }

    return (
      this.teamMembers.find((member: TeamMember) => {
        return member.sub === userId;
      })?.email || ''
    );
  }

  public formatLoadedDefaults(defaults: any[]) {
    // formats certain fields when loaded
    const cutOffIndex = defaults.findIndex(
      defaultAttribute => defaultAttribute.key === 'CutOff'
    );
    const cutOff = defaults[cutOffIndex];

    if (cutOff) {
      cutOff.value =
        this.productsConfigService.cutOffTimesAndFixingSources.cutOffs.find(
          y => y.code === cutOff.value
        )?.longName;
      if (!cutOff.value) {
        defaults.splice(cutOffIndex, 1);
      }
    }

    const fixingSourceIndex = defaults.findIndex(
      defaultAttribute => defaultAttribute.key === 'Fixing Source'
    );
    const fixingSource = defaults[fixingSourceIndex];

    if (fixingSource) {
      fixingSource.value =
        this.productsConfigService.cutOffTimesAndFixingSources.fixingSources.find(
          y => y.code === fixingSource.value
        )?.longName;
      if (!fixingSource.value) {
        defaults.splice(fixingSourceIndex, 1);
      }
    }
  }

  public setTeamMembers() {
    this.authService.currentSession().then(data => {
      // TODO: this assumes user only belongs to one group,
      // change this to take the group name that is compared to
      // a list of acceptable groupnames (record in config table?)
      const payload = data.getIdToken().payload;
      const params = {
        task: 'loadUsers'
      };
      this.authService
        .fxPost<any>(params, `get-user-info`, params)
        .pipe(catchError(this.handleError.bind(this)))
        .subscribe({
          next: (resp: { users: TeamMember[] }) => {
            this._teamMembers = resp.users
              .map(user => {
                const { email, sub } = user; // remove extra fields from runtime data
                return { email, sub } as TeamMember;
              })
              .filter(y => {
                // exclude the current user
                return y.email !== payload.email;
              });
          },
          error: error => this.handleError(error)
        });
    });
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,

      this.alertsService.addAlert({ message: error.error });

      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }
}
